import Button from 'tio-reimagined/components/button';
import { eq } from 'tio-ui/utilities';
import { fn, hash } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { Section, VStack } from 'tio-reimagined/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AccountsLinkedLoansSummary from '../../accounts/linked-loans-summary';
import Component from '@glimmer/component';
import SvgJar from 'ember-svg-jar/helpers/svg-jar';
import type { IntlService } from 'ember-intl';
import type { LoanSummary, SummaryByLoanType } from 'tio-common/types/loan-summary';
import type AccountModel from 'tio-data/models/account';
import type PersonModel from 'tio-data/models/person';
import type RouterService from 'tio-employee/services/router';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import { PhCheck } from 'ember-phosphor-icons';
import type { ObservableSource } from 'tio-employee/services/observability';

export interface WhatHappensNextSignature {
  Args: {
    accounts: AccountModel[];
    person: PersonModel;
    loanSummary: LoanSummary;
    programType: string;
  };
  Element: HTMLElement;
}

class RecResultsWhatHappensNextComponent extends Component<WhatHappensNextSignature> {
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare intl: IntlService;

  @tracked showMissingInfoDialog = false;
  @tracked showEstimatorBlockDialog = false;
  @tracked showGraduatedPlanBanner = false;

  checkLoansAndGoToEstimator = () => {
    const summary = this.args.loanSummary;
    const loanErrors = summary.errors || [];
    const noLoanErrors = loanErrors.length === 0;

    const hasSummaryByLoanType = summary.summaryByLoanType.length > 0;
    const hasGraduatedPlan = summary.summaryByLoanType.some(
      (typeSummary: SummaryByLoanType) => typeSummary.repaymentPlan === 'graduated'
    );

    if (this.args.programType === 'sf' && hasGraduatedPlan) {
      this.showGraduatedPlanBanner = true;
      return;
    }

    if (!hasSummaryByLoanType) {
      this.showEstimatorBlockDialog = true;
      return;
    }

    if (noLoanErrors) {
      return this.args.programType === 'sf'
        ? this.router.transitionTo(
            'authenticated.repaying-student-debt.repayment-strategy-finder.dashboard.questionnaire'
          )
        : this.router.transitionTo('authenticated.idr.dashboard.forgiveness-estimator');
    } else {
      this.showMissingInfoDialog = true;
    }
  };

  get source(): ObservableSource {
    switch (this.args.programType) {
      case 'idr':
        return 'idr';
      case 'sf':
        return 'strategy_finder';
      default:
        return 'pslf';
    }
  }

  <template>
    <Section>
      <:header>{{t "idr.results.what_happens_next"}}</:header>
      <:body>
        <VStack class="flex flex-col justify-center md:justify-start">
          <div class="max-w-24">
            {{SvgJar
              "crystal-ball"
              role="img"
              width="100%"
              height="100%"
              desc=(t "svg.crystal_ball")
            }}
          </div>
          <div class="flex">
            <PhCheck @size="32" weight="fill" class="tio-fg-success mx-1" />
            <p class="mr-4">{{t "idr.results.recertify"}}</p>
          </div>
          <div class="flex justify-center md:justify-start">
            <PhCheck @size="32" weight="fill" class="tio-fg-success mx-1" />
            <p class="mr-2">{{t "idr.results.financial_circumstances"}}</p>
          </div>
          <Button class="mr-auto" @intent="primary" {{on "click" this.checkLoansAndGoToEstimator}}>
            {{#if (eq @programType "sf")}}
              {{t "common.strategy_finder.default"}}
            {{else}}
              {{t "idr.results.payment_button"}}
            {{/if}}
          </Button>
        </VStack>
      </:body>
    </Section>
    {{! TODO: Could be one modal who's text is deisplayed based on state}}
    <Modal
      @isOpen={{this.showEstimatorBlockDialog}}
      @onClose={{fn (mut this.showEstimatorBlockDialog) false}}
      class="min-h-64"
    >
      <:body>
        {{#if (eq @programType "sf")}}
          <p class="text-xl text-center tio-fg-secondary font-semibold mb-4">
            {{t "strategy_finder.before_proceeding"}}
          </p>
          <p>{{t "strategy_finder.we_will_not_be_able"}}</p>
        {{else}}
          <p class="text-xl text-center tio-fg-secondary font-semibold mb-4">
            {{t "pslf_dashboard.actions.dialog.before_proceeding"}}
          </p>
          <p>{{t "pslf_dashboard.actions.dialog.we_will_not_be_able"}}</p>
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{fn (mut this.showEstimatorBlockDialog) false}}
          @submitText={{t "ok"}}
        />
      </:footer>
    </Modal>
    <Modal
      @isOpen={{this.showGraduatedPlanBanner}}
      @onClose={{fn (mut this.showGraduatedPlanBanner) false}}
      class="min-h-64"
    >
      <:body>
        <p class="text-xl text-center tio-fg-secondary font-semibold mb-4">
          {{t "pslf_dashboard.actions.dialog.graduated_plan"}}
        </p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{fn (mut this.showEstimatorBlockDialog) false}}
          @submitText={{t "ok"}}
        />
      </:footer>
    </Modal>
    <Modal
      @isOpen={{this.showMissingInfoDialog}}
      @onClose={{fn (mut this.showMissingInfoDialog) false}}
      class="min-h-64 lg:min-w-5xl"
      @size="xl"
    >
      <:header>
        {{@person.firstName}},
        {{t "pslf_dashboard.actions.dialog.we_need_your_help"}}
      </:header>
      <:body>
        <p class="text-tio-gray-400 mb-5">
          <LinkTo
            @route="authenticated.observability.upload"
            @query={{hash source=this.source providers="nslds"}}
            class="tio-copy"
          >
            {{t "pslf_dashboard.actions.dialog.upload_mystudentdata_to_add_missing_info"}}
          </LinkTo>
        </p>
        <AccountsLinkedLoansSummary
          @accounts={{@accounts}}
          @loanSummary={{@loanSummary}}
          @person={{@person}}
          @source={{this.source}}
        />
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{this.checkLoansAndGoToEstimator}}
          @submitText={{t "common.continue"}}
          @onCancel={{fn (mut this.showMissingInfoDialog) false}}
        />
      </:footer>
    </Modal>
  </template>
}

export default RecResultsWhatHappensNextComponent;
